import React, { useState } from 'react';
import { galleryImages } from '../data/mock';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const Gallery = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [showAfter, setShowAfter] = useState(true);

  const nextSlide = () => {
    setActiveIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevSlide = () => {
    setActiveIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  return (
    <section id="gallery" className="py-24 bg-gradient-to-b from-white to-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-cyan-100 text-cyan-700 text-sm font-semibold mb-4">
            Our Work
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-800 mb-6">
            Before &{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-500 to-blue-600">
              After
            </span>{' '}
            Transformations
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            See the remarkable difference our expertise makes. Browse through our portfolio
            of stunning pool transformations.
          </p>
        </div>

        {/* Gallery Carousel */}
        <div className="relative max-w-4xl mx-auto">
          <div className="relative aspect-[16/10] rounded-3xl overflow-hidden shadow-2xl bg-slate-200">
            {/* Image */}
            <img
              src={showAfter ? galleryImages[activeIndex].after : galleryImages[activeIndex].before}
              alt={galleryImages[activeIndex].title}
              className="w-full h-full object-cover transition-opacity duration-500"
            />

            {/* Before/After Toggle */}
            <div className="absolute top-6 left-1/2 -translate-x-1/2 z-10">
              <div className="flex items-center gap-1 p-1 rounded-full bg-white/90 backdrop-blur-sm shadow-lg">
                <button
                  onClick={() => setShowAfter(false)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    !showAfter
                      ? 'bg-cyan-500 text-white shadow-md'
                      : 'text-slate-600 hover:text-slate-800'
                  }`}
                >
                  Before
                </button>
                <button
                  onClick={() => setShowAfter(true)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    showAfter
                      ? 'bg-cyan-500 text-white shadow-md'
                      : 'text-slate-600 hover:text-slate-800'
                  }`}
                >
                  After
                </button>
              </div>
            </div>

            {/* Project Title */}
            <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/60 to-transparent">
              <h3 className="text-xl font-semibold text-white">
                {galleryImages[activeIndex].title}
              </h3>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/90 backdrop-blur-sm shadow-lg flex items-center justify-center text-slate-700 hover:bg-white hover:text-cyan-600 transition-all"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/90 backdrop-blur-sm shadow-lg flex items-center justify-center text-slate-700 hover:bg-white hover:text-cyan-600 transition-all"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>

          {/* Dots Navigation */}
          <div className="flex items-center justify-center gap-3 mt-8">
            {galleryImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveIndex(index)}
                className={`w-3 h-3 rounded-full transition-all ${
                  index === activeIndex
                    ? 'bg-cyan-500 w-8'
                    : 'bg-slate-300 hover:bg-slate-400'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Thumbnail Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-12">
          {galleryImages.map((image, index) => (
            <button
              key={image.id}
              onClick={() => setActiveIndex(index)}
              className={`group relative aspect-video rounded-2xl overflow-hidden shadow-lg transition-all ${
                index === activeIndex
                  ? 'ring-4 ring-cyan-500 ring-offset-4'
                  : 'hover:shadow-xl'
              }`}
            >
              <img
                src={image.after}
                alt={image.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end p-4">
                <span className="text-white text-sm font-medium">{image.title}</span>
              </div>
              <div className="absolute inset-0 bg-cyan-500/0 group-hover:bg-cyan-500/20 transition-colors flex items-center justify-center">
                <ArrowRight className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Gallery;
